import { ExclamationCircleOutlined } from "@ant-design/icons";
import { Button, Modal, Popconfirm, Popover, Select, Space, Tabs } from "antd";
import { useForm } from "antd/es/form/Form";
import PropTypes from "prop-types";
import { useQueryClient } from "@tanstack/react-query";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import "../assets/scss/projects.scss";
import CRUDTabTableModal from "../components/CRUDTabTableModal";
import CustomForm from "../components/CustomForm";
import FormButtonCancel from "../components/FormButtonCancel";
import FormButtonSave from "../components/FormButtonSave";
import DeliverableConfigurationModal from "../components/Modal/DeliverableConfigurationModal";
import TaskConfigurationModal from "../components/Modal/TaskConfigurationModal";
import TemplateDisabledModal from "../components/Modal/TemplateDisabledModal";
import LessonLearnedImportModal from "../components/Modal/LessonLearnedImportModal";
import usePermission from "../hooks/usePermission";
import { updateDeliverableDisabled } from "../services/api-server/deliverables";
import {
	getOperations,
	updateOperationItem,
} from "../services/api-server/operations";
import { updatePhaseDisabled } from "../services/api-server/phases";
import { getCampaignTasks, getProject } from "../services/api-server/projects";
import { updateTaskDisabled } from "../services/api-server/tasks";
import { getWells } from "../services/api-server/wells";
import { TProjectData } from "../types/project";
import { GetAntIcon } from "../utils/ant_icons";
import Emitter from "../utils/emitter";
import { getCrudaClass } from "../utils/lookup_list";
import { checkPrivilege } from "../utils/utils";
import { HomeContext } from "./Home";
import NoAccess from "./NoAccess";
import LockedModuleTooltip from "../components/LockedModuleTooltip";
import useCheckModule from "../hooks/useCheckModule";

// import ProjectDetails from "../projectTabs/ProjectDetails";
function OperationSettings(props: any) {
	const queryClient = useQueryClient();
	const context: any = useContext(HomeContext);
	const location = useLocation();
	const navigate = useNavigate();
	const [allData, setAllData] = useState<any>({});

	const [projectInfo, setProjectInfo] = useState<any>(null);
	const [wellInfo, setWellInfo] = useState<any>(null);
	const [isLoading, setIsLoading] = useState<any>(true);

	const [phaseTaskRecord, setPhaseTaskRecord] = useState<any>(null);
	const [deliverableRecord, setDeliverableRecord] = useState<any>(null);
	const [taskModalOpen, setTaskModalOpen] = useState<any>(false);
	const [deliverableModalOpen, setDeliverableModalOpen] = useState<any>(false);

	const [allOperation, setAllOperation] = useState<any>([]);
	const [operationId, setOperationId] = useState<any>(null);
	const [operationCode, setOperationCode] = useState<any>(null);
	const [formRef]: any = useForm();
	const [formReset, setFormReset] = useState<boolean>(true);
	const [emptyModalOpen, setEmptyModalOpen] = useState<any>(null);

	const [initialForm, setInitialForm] = useState<any>(null);
	const [popOverVisibility, setPopOverVisibility] = useState<boolean>(false);
	const [dropdownOpen, setDropdownOpen] = useState<any>(false);

	const [otherData, setOtherData] = useState<any>(null);
	const [deliverableExtraData, setDeliverableExtraData] = useState<any>(null);
	const [allCampaignTasks, setAllCampaignTasks] = useState<any>(null);
	const [filteredDataOptions, setFilteredDataOptions] = useState<any>(null);

	const [templateModalOpen, setTemplateModalOpen] = useState<any>(false);
	const [templateObject, setTemplateObject] = useState<any>(null);
	const [importModalOpen, setImportModalOpen] = useState(false);

	const navTabKey = location?.state?.tabKey;
	const navTabPayload = location?.state?.phaseRecord;

	const hasEditOperationPermission = usePermission(
		"Edit.Operation",
		projectInfo?.project_code
	);
	const hasDeleteOperationPermission = usePermission(
		"Delete.Operation",
		projectInfo?.project_code
	);

	const { checkModule } = useCheckModule();

	const tabList: any = [
		{ label: "Operation details", key: "well-project-details" },
		{
			label: !checkModule("project-delivered") ? (
				<LockedModuleTooltip>
					<div style={{ marginRight: "20px" }}>Well programme</div>
				</LockedModuleTooltip>
			) : (
				"Well programme"
			),
			disabled: !checkModule("project-delivered"),
			key: "well-programme",
		},
	];
	const [tabKey, setTabKey] = useState<any>(() => {
		if (navTabKey) {
			return navTabKey;
		} else if (sessionStorage.getItem("main-tabkey")) {
			let sessionTabKey: any = sessionStorage.getItem("main-tabkey");
			let found = tabList.find((tab: any) => {
				return tab.key === sessionTabKey;
			});
			if (found) {
				return sessionTabKey;
			} else {
				sessionStorage.setItem("main-tabkey", "well-project-details");
				return "well-project-details";
			}
		} else {
			sessionStorage.setItem("main-tabkey", "well-project-details");
			return "well-project-details";
		}
	});

	// const [history, setHistory] = useState<any>([]);
	// const [sideContainerOpen, setSideContainerOpen] = useState<any>(false);

	// Load all the data needed for the form
	useEffect(() => {
		const fetchCampaignTasks = async (project: TProjectData) => {
			if (project?._id) {
				const campaignTasks = await getCampaignTasks(
					context.currentTenantKey,
					project._id
				);
				setAllCampaignTasks(campaignTasks);
			}
		};
		if (context.currentTenantKey && context.allData) {
			let project_code: any =
				location?.state?.project_code || location?.pathname?.split("/")[2];
			let operation_code: any =
				location?.state?.operation_code || location?.pathname?.split("/")[3];

			getProject(context.currentTenantKey, project_code)
				.then((project: any) => {
					let allData: any = project;
					setProjectInfo(project);
					context.setCurrentProject(project);
					fetchCampaignTasks(project);
					getOperations(context.currentTenantKey, { project: project._id })
						.then((data: any) => {
							setAllOperation(data);
							let foundOperation = data.find(
								(operation: any) => operation?.operation_code === operation_code
							);

							if (foundOperation) {
								context.setCurrentOperation(foundOperation);
								getWells(context.currentTenantKey, {
									_id: foundOperation?.well_id,
								})
									.then((wellData: any) => {
										const wellOperationData: any = {
											...wellData[0],
											...foundOperation,
										};
										setWellInfo(wellData[0]);
										resetForm(wellOperationData);
										setOperationCode(operation_code);
										setOperationId(foundOperation?._id);
										setOtherData({
											operation_id: foundOperation?._id,
											operation_code: operation_code,
											project_id: project?._id,
											project_code: project?.project_code,
										});
									})
									.catch((error: any) => {});

								// Filter allData.role_assignments to remove data that does not exist in context.allData.roles if roles in context.allData exist and not empty
								if (allData.role_assignments) {
									if (
										context.allData.roles &&
										context.allData.roles.length > 0
									) {
										allData.role_assignments = allData.role_assignments.filter(
											(role: any) => {
												return context.allData.roles.find(
													(item: any) => item._id == role.role_id
												);
											}
										);
									}
								}

								// Then push default project manager role id project manager exists
								if (allData.project_manager) {
									if (allData.role_assignments) {
										allData.role_assignments.push({
											role: "Project manager",
											role_id: "project_manager",
											appointee: [...allData.project_manager],
										});
									} else {
										allData.role_assignments = [
											{
												role: "Project manager",
												role_id: "project_manager",
												appointee: [...allData.project_manager],
											},
										];
									}
								}
								setDeliverableExtraData({
									...allData,
								});
								setAllData({ ...context.allData, ...allData });
							} else {
								setWellInfo(null);
							}
						})
						.catch((error: any) => {
							setWellInfo(null);
						})
						.finally(() => {
							setIsLoading(false);
						});
				})
				.catch(() => {
					setProjectInfo(null);
					setIsLoading(false);
				});
		}
	}, [context.currentTenantKey, context.allData, location.pathname]);

	// Emitter addition
	useEffect(() => {
		Emitter.on("taskModalOpen", async (payload: any) => {
			try {
				setPhaseTaskRecord(payload);
				setTaskModalOpen(true);
			} catch (error) {}
		});

		Emitter.on("templateDisableModalOpen", (payload: any) => {
			setTemplateObject({
				title: payload.title,
				message: payload.message,
				id: payload.id,
				level: payload.level,
			});
			setTemplateModalOpen(true);
		});

		//Help me
		Emitter.on("templateForceSave", (payload: any) => {
			handleTemplateModalSave(payload.id, payload.level, {
				operation_code: operationCode,
				disabled: false,
				disabled_reason: "",
			});
		});

		return () => {
			Emitter.remove("taskModalOpen");
			Emitter.remove("templateDisableModalOpen");
			Emitter.remove("templateForceSave");
		};
	}, [
		context.currentTenantKey,
		phaseTaskRecord,
		deliverableRecord,
		operationCode,
	]);

	useEffect(() => {
		if (navTabKey === "well-programme") {
			Emitter.emit("taskModalOpen", {
				...navTabPayload,
			});
		}
	}, [navTabKey]);

	// Handling Task Modal close
	const handleTaskModalClose = () => {
		setPhaseTaskRecord(null);
	};

	// Force table re-render
	// Have to figure out a way to update just one specific task to the table renderer
	const handleTaskFormUpdate = (payload: any) => {
		setOtherData({
			project_code: projectInfo?.project_code,
			project_id: projectInfo?._id,
			operation_code: operationCode,
			operation_id: operationId,
		});
		if (payload) {
			setPhaseTaskRecord({
				...phaseTaskRecord,
				task: payload,
			});
		}
	};

	const handleDeliverableModalClose = () => {
		context?.handleFormUpdating(
			false,
			deliverableRecord?._id || "deliverable_new"
		);
		setDeliverableModalOpen(false);
		setDeliverableRecord(null);
	};

	const handleDeliverableFormUpdate = (payload: any = null) => {
		setOtherData({
			project_id: projectInfo?._id,
			project_code: projectInfo?.project_code,
			operation_code: operationCode,
			operation_id: operationId,
		});
		//Force re-render to download deliverable data
		setPhaseTaskRecord({
			...phaseTaskRecord,
			task: { ...phaseTaskRecord.task },
		});
		setDeliverableRecord(payload);
	};

	const handleTemplateModalSave = (id: any, level: any, payload: any) => {
		Emitter.emit("loading", {
			name: "operation-settings",
			isLoading: true,
		});
		payload.operation_code = operationCode;
		if (level === "phase") {
			updatePhaseDisabled(
				context?.currentTenantKey,
				id,
				payload,
				props.params?.user
			)
				.then((result: any) => {
					Emitter.emit("forceUpdate", { data: payload, level: level });
					setTemplateModalOpen(false);
				})
				.catch((error: any) => {
					console.log(error);
				})
				.finally(() => {
					Emitter.emit("loading", {
						name: "operation-settings",
						isLoading: false,
					});
				});
		} else if (level === "task") {
			updateTaskDisabled(
				context?.currentTenantKey,
				id,
				payload,
				props.params?.user
			)
				.then((result: any) => {
					setPhaseTaskRecord({
						...phaseTaskRecord,
						task: { ...phaseTaskRecord?.task, ...payload },
					});
					Emitter.emit("forceUpdateTask", null);

					setTemplateModalOpen(false);
				})
				.catch((error: any) => {
					console.log(error);
				})
				.finally(() => {
					Emitter.emit("loading", {
						name: "operation-settings",
						isLoading: false,
					});
				});
		} else if (level === "deliverable") {
			updateDeliverableDisabled(
				context?.currentTenantKey,
				id,
				payload,
				props.params?.user
			)
				.then((result: any) => {
					setDeliverableRecord({ ...deliverableRecord, ...payload });
					Emitter.emit("forceUpdateDeliverable", {
						...deliverableRecord,
						...payload,
					});
					setTemplateModalOpen(false);
				})
				.catch((error: any) => {
					console.log(error);
				})
				.finally(() => {
					Emitter.emit("loading", {
						name: "operation-settings",
						isLoading: false,
					});
				});
		}
	};

	// reset form
	const resetForm = (values: any = null, data: any = null) => {
		if (formRef) {
			setFormReset(true);
			formRef.resetFields();
			formRef.setFieldsValue(values);

			let filteredExtraData: any = {};
			// get the value of `well_purpose` field from the form
			if (formRef.getFieldValue("well_purpose")) {
				let lookup = context.allData;
				// In `context.allData`, find the `well_purpose` array object
				let foundData = lookup?.well_purpose?.find(
					//match any `uuid` that is equal to the value of `well_purpose` field from the form
					(element: any) =>
						element.uuid === formRef.getFieldValue("well_purpose")
				);
				// If found
				if (foundData) {
					// Add the `lookup.well_purpose_type` array to the `filteredExtraData.well_purpose_type` object
					filteredExtraData.well_purpose_type = lookup?.well_purpose_type
						?.map((element: any) => {
							// If any of the `uuid` in the `foundData.types` array is equal to the `uuid` in the `element` object,
							if (foundData.types.includes(element.uuid)) return element;
						})
						// Filter out any falsy values like `null`, `undefined`, `0`, `""`
						.filter(Boolean);
				}
			}
			if (formRef.getFieldValue("well_status")) {
				let lookup = context.allData;
				let foundData = lookup?.well_status?.find(
					(element: any) =>
						element.uuid === formRef.getFieldValue("well_status")
				);
				if (foundData) {
					filteredExtraData.well_status_type = lookup?.well_status_type
						?.map((element: any) => {
							if (foundData.types.includes(element.uuid)) return element;
						})
						.filter(Boolean);
				}
			}

			// if filteredExtraData has any keys (properties)
			if (Object.keys(filteredExtraData).length > 0) {
				// Duplicate `context.allData` and `filteredExtraData` into `filteredDataOptions` state
				setFilteredDataOptions({ ...context.allData, ...filteredExtraData });
			}
		}
	};

	// Function to sort array of objects alphabetically based on property's name
	const sortData = (data: any) => {
		data.sort((a: any, b: any) => {
			if (a.label.toLowerCase() < b.label.toLowerCase()) {
				return -1;
			}
			if (a.label.toLowerCase() > b.label.toLowerCase()) {
				return 1;
			}
			return 0;
		});
		return data;
	};

	// Handle when project has change / adding a new project
	const handleProjectChange = async (
		key: any = null,
		data: any = null,
		manual: boolean = false
	) => {
		try {
			let projects = data?.projects || allOperation;
			await context.handlePageChange();
			Emitter.emit("loading", {
				name: "operation-settings",
				isLoading: true,
			});
			setOperationCode(key);
			setOperationId(null);
			navigate(
				`/projects/${projectInfo?.project_code}/${key}/operation-settings`
			);

			//Always set tabKey to project-details when project has changed
			if (manual) {
				setTabKey("well-project-details");
				sessionStorage.removeItem("tabKey");
			}

			if (key == "Add new project" || key == null) {
				Emitter.emit("projectName", "Add new project");
				setTabKey("well-project-details");
				sessionStorage.removeItem("tabKey");
				resetForm(null);
			} else {
				const project = projects.find((project: any) => {
					// Emit the project name to set the breadcrumb
					Emitter.emit("projectName", project.project_name);
					return project.project_code == key;
				});
				resetForm(project, data);
			}
			Emitter.emit("loading", {
				name: "operation-settings",
				isLoading: false,
			});
		} catch {}
	};

	const handleTabChange = async (key: any) => {
		try {
			await context.handlePageChange();
			setTabKey(key);
			setFormReset(true);
			sessionStorage.setItem("main-tabkey", key);
			const operation = allOperation.find((operation: any) => {
				return operation.operation_code == operationCode;
			});
			getWells(context.currentTenantKey, {
				_id: operation?.well_id,
			})
				.then((wellData: any) => {
					const wellOperationData: any = {
						...wellData[0],
						...operation,
					};
					resetForm(wellOperationData);
				})
				.catch((error: any) => {
					console.log(error);
				});
		} catch {}
	};

	// Handle form save
	const handleFormSave = (formValues: any | null = null, type: any = null) => {
		let values = formValues || formRef.getFieldsValue();

		// For each values in the form, trim the leading and trailing spaces
		Object.keys(values).forEach((key) => {
			if (typeof values[key] == "string") {
				values[key] = values[key].trim();
			}
		});

		Emitter.emit("loading", {
			name: "operation-settings",
			isLoading: true,
		});

		values.operation_code = operationCode;
		getCrudaClass(tabKey).then((crudaClassInstance: any) => {
			if (operationCode && wellInfo) {
				const form = crudaClassInstance.getFormList(tabKey);
				const formList: any = [];
				form.forEach((element: any) => {
					if (element.children && !("name" in element)) {
						element.children.forEach((child_element: any) => {
							if (child_element.name) formList.push(child_element.name);
						});
					} else {
						if (element.name) formList.push(element.name);
					}
				});

				values.modified_on = Date.now();
				values.project_code = projectInfo.project_code;
				updateOperationItem(
					context.currentTenantKey,
					wellInfo._id,
					values,
					initialForm,
					props.params.user,
					formList
				)
					.then((updatedData: any) => {
						Emitter.emit("alert", {
							type: "success",
							message: "Operation details has been successfully updated.",
							description: "",
							top: true,
							closeable: false,
							timeout: 3000,
						});
						getWells(context.currentTenantKey, {
							_id: updatedData?.well_id,
						})
							.then((wellData: any) => {
								const wellOperationData: any = {
									...wellData[0],
									...updatedData,
								};
								resetForm(wellOperationData);
							})
							.catch((error: any) => {
								console.log(error);
							});
						context.setCurrentOperation(updatedData);

						// I dont know why its here so i just gonna leave it
						getOperations(context.currentTenantKey, {})
							.then((_data: any) => {
								setAllOperation(_data);
							})
							.catch((error: any) => {
								console.log(error);
							});
					})
					.catch((error: any) => {
						Emitter.emit("alert", {
							type: "error",
							message: "Failed to update operation. Please try again.",
							description: "",
							top: true,
							closeable: false,
							timeout: 3000,
						});
						resetForm();
					})
					.finally(() => {
						Emitter.emit("loading", {
							name: "operation-settings",
							isLoading: false,
						});
					});
			}
		});
	};

	const handleFormDelete = (project_code: any) => {
		Emitter.emit("loading", {
			name: "operation-settings",
			isLoading: true,
		});
		context?.handleFormUpdating(false);
		updateOperationItem(
			context.currentTenantKey,
			wellInfo._id,
			{ is_removed: true },
			null,
			props.params.user,
			null
		)
			.then(() => {
				Emitter.emit("alert", {
					type: "success",
					message: "Operation has been successfully deleted.",
					description: "",
					top: true,
					closeable: false,
					timeout: 3000,
				});
				resetForm();
			})
			.catch((error: any) => {
				console.log("error", error);
				Emitter.emit("alert", {
					type: "error",
					message: "Failed to delete operation. Please try again.",
					description: "",
					top: true,
					closeable: false,
					timeout: 3000,
				});
			})
			.finally(() => {
				Emitter.emit("loading", {
					name: "operation-settings",
					isLoading: false,
				});
				navigate(`/projects/${projectInfo?.project_code}`, { replace: true });
			});
	};

	// Handle form Cancel
	const handleFormCancel = () => {
		context?.handleFormUpdating(false);
		if (operationCode != "Add new project") {
			const project = allOperation.find((project: any) => {
				return project.operation_code == operationCode;
			});
			resetForm(project);
		} else {
			resetForm();
		}
	};

	const tabContent = () => {
		switch (tabKey) {
			// Operation details tab contents
			case "well-project-details":
				return (
					<>
						{context?.allData && (
							<div
								style={{
									flex: "1",
									display: "flex",
									flexDirection: "row",
									overflow: "auto",
								}}
							>
								<div
									className="main-container"
									style={{
										width: "70%",
										display: "flex",
										flexDirection: "column",
										borderRadius: "3px",
									}}
								>
									<div
										className="generic-header"
										style={{
											height: "50px",
											display: "flex",
											justifyContent: "space-between",
											alignItems: "center",
											paddingRight: "20px",
										}}
									>
										<span>Settings</span>
										<Button
											type="primary"
											onClick={() => setImportModalOpen(true)}
										>
											Import Lessons
										</Button>
									</div>
									<div className="generic-content" style={{ flex: "1" }}>
										<div style={{ flex: "1", position: "relative" }}>
											{/* {operationCode !== "Add new project" &&
										operationCode && (
											<span
												style={{
													position: "absolute",
													right: 30,
													color: "rgba(255, 255, 255, 0.5)",
												}}
											>
												# {operationCode}
											</span>
										)} */}
											{/* Add Form here */}
											<CustomForm
												setFormReset={setFormReset}
												handleFormSave={handleFormSave}
												formRef={formRef}
												tabKey={tabKey}
												filteredDataOptions={filteredDataOptions}
												dataOptions={context.allData}
												activeRowKey={wellInfo._id}
												activeRecord={wellInfo}
												readOnly={!hasEditOperationPermission.hasPermission}
											/>
										</div>
									</div>
									<div className="generic-footer" style={{ height: "50px" }}>
										{hasDeleteOperationPermission.hasPermission &&
											operationCode && (
												<Popconfirm
													overlayInnerStyle={{
														maxWidth: "300px",
														padding: "16px",
													}}
													placement="topLeft"
													okText="Delete"
													okType="danger"
													title=""
													icon={
														<ExclamationCircleOutlined
															style={{ color: "grey" }}
														/>
													}
													cancelText={"Cancel"}
													cancelButtonProps={{ ghost: true }}
													overlayClassName="popconfirm-danger"
													description="Permanently delete this operation? This action cannot be undone."
													onOpenChange={(open: any) => {
														if (!open) {
															setEmptyModalOpen(false);
														}
													}}
													onConfirm={() => {
														setEmptyModalOpen(false);
														handleFormDelete(operationCode);
													}}
													onCancel={() => {
														setEmptyModalOpen(false);
													}}
												>
													<Button
														className="ant-btn-secondary"
														style={{ marginRight: "auto" }}
														danger
													>
														Delete
													</Button>
												</Popconfirm>
											)}

										{hasEditOperationPermission.hasPermission && (
											<>
												<FormButtonCancel
													form={formRef}
													handleFormCancel={handleFormCancel}
													formReset={formReset}
												></FormButtonCancel>
												<Popover
													open={popOverVisibility}
													onOpenChange={(boolean: any) => {
														// if (checkCode()) {
														// 	setPopOverVisibility(boolean);
														// } else {
														// 	setPopOverVisibility(false);
														// }
													}}
													overlayStyle={{
														maxWidth: "420px",
														background: "rgba(57,57,57,1)",
														borderRadius: "8px",
														// padding: "16px",
													}}
													style={{ background: "transparent" }}
													trigger={"click"}
													content={
														<div
															style={{
																display: "flex",
																flexDirection: "row",
																gap: "8px",
															}}
														>
															<Button
																className="ant-button-default"
																style={{
																	marginRight: "auto",
																	border: "1px solid rgba(255, 255, 255, 0.1)",
																}}
																ghost
																onClick={() => {
																	setPopOverVisibility(false);
																	handleFormSave(null, "New");
																}}
															>
																Create new
															</Button>
															<Button
																className="ant-button-default"
																style={{
																	border: "1px solid rgba(255, 255, 255, 0.1)",
																}}
																ghost
																onClick={() => {
																	setPopOverVisibility(false);
																}}
															>
																Cancel
															</Button>
															<Button
																className="ant-button-default"
																onClick={() => {
																	setPopOverVisibility(false);
																	handleFormSave(null, "code");
																}}
															>
																Proceed
															</Button>
														</div>
													}
													title={
														<Space>
															<div style={{ color: "grey" }}>
																{GetAntIcon("exclamation")}
															</div>
															<span style={{ fontWeight: "400" }}>
																Changing the business unit or client info
																affects the project code. Do you wish to
																proceed?
															</span>
														</Space>
													}
												>
													<div>
														<FormButtonSave
															form={formRef}
															handleFormSave={handleFormSave}
															setInitialForm={setInitialForm}
															formReset={formReset}
															setFormReset={setFormReset}
														></FormButtonSave>
													</div>
												</Popover>
											</>
										)}
									</div>
								</div>
							</div>
						)}
						<LessonLearnedImportModal
							open={importModalOpen}
							onClose={() => setImportModalOpen(false)}
							onSuccess={(importedCount: number) => {
								Emitter.emit("alert", {
									type: "success",
									message: "Import successful",
									description: `${importedCount} lesson${importedCount !== 1 ? "s" : ""} learned imported successfully.`,
									top: true,
									closeable: false,
									timeout: 5000,
								});
								queryClient.invalidateQueries({
									queryKey: ["reporting-hub-data", "lesson_learned"],
								});
								setImportModalOpen(false);
							}}
							tenantKey={context.currentTenantKey}
							operationContext={
								operationId
									? {
											operation_id: operationId,
											project_code: projectInfo?.project_code,
											well_id: wellInfo?._id,
										}
									: undefined
							}
						/>
					</>
				);

			// Well programme tab contents
			case "well-programme":
				return (
					<CRUDTabTableModal
						items={[
							{
								label: "Phases and tasks",
								key: "phase",
								directoryName: "Phases and tasks directory",
								configurationName: "Phase configuration",
								sortKey: "sequence_id",
								formOption:
									projectInfo?.project_scope === "multi-asset"
										? "multi-asset-operation"
										: null,
							},
						]}
						tabKey={"phase"}
						otherData={otherData}
						user={props.params?.user}
						userPermissions={
							projectInfo?.project_scope === "multi-asset"
								? ["add-phase"]
								: ["update", "add", "add-phase", "delete"]
						}
						reorderModal
						copyTemplateModal={
							projectInfo?.project_scope === "multi-asset"
								? null
								: checkPrivilege(props?.params?.userRole, ["tenant-admin"])
						}
					/>
				);

			default:
				return <></>;
		}
	};

	if (
		hasEditOperationPermission.isLoading ||
		hasDeleteOperationPermission.isLoading
	) {
		return <></>;
	}

	if (
		!hasEditOperationPermission.hasPermission &&
		!hasDeleteOperationPermission.hasPermission
	) {
		return (
			<NoAccess text="Oops, looks like you don't have the authorisation to view this page." />
		);
	}

	return (
		<>
			{projectInfo && wellInfo && !isLoading && (
				<div className="generic-container well-settings">
					<Space direction="vertical">
						<Select
							getPopupContainer={(trigger: any) => trigger.parentNode}
							className="title-selector"
							style={{
								fontSize: "20px",
								fontWeight: "600",
								width: "auto",
							}}
							options={allOperation}
							value={operationCode}
							open={dropdownOpen}
							onClick={() => {
								setDropdownOpen(!dropdownOpen);
							}}
							onChange={(key: any) => {
								handleProjectChange(key, null, true);
								setDropdownOpen(false);
							}}
							onBlur={(event: any) => {
								event.preventDefault();
								setDropdownOpen(false);
							}}
							fieldNames={{
								label: "operation_name",
								value: "operation_code",
							}}
							dropdownStyle={{ minWidth: "100%", width: "auto" }}
							dropdownRender={(menu) => {
								return <>{menu}</>;
							}}
						></Select>

						<Tabs
							activeKey={tabKey}
							onChange={handleTabChange}
							items={tabList}
						></Tabs>
					</Space>
					<div
						style={{
							flex: "1",
							display: "flex",
							flexDirection: "row",
							gap: "20px",
							maxHeight: "100%",
							overflow: "auto",
						}}
					>
						{operationCode && tabContent()}
					</div>

					<Modal
						className="empty-modal"
						open={emptyModalOpen}
						closable={false}
						maskClosable={true}
						onCancel={() => {
							setEmptyModalOpen(false);
						}}
						zIndex={1050}
						footer={<></>}
						style={{ background: "transparent" }}
					>
						<></>
					</Modal>
					{phaseTaskRecord?._id && allCampaignTasks && (
						<TaskConfigurationModal
							open={taskModalOpen}
							option={
								projectInfo?.project_scope === "multi-asset"
									? "multi-asset"
									: null
							}
							setOpen={setTaskModalOpen}
							handleFormUpdate={handleTaskFormUpdate}
							handleModalClose={handleTaskModalClose}
							setDeliverableModalOpen={setDeliverableModalOpen}
							setDeliverableRecord={setDeliverableRecord}
							currentProject={projectInfo}
							otherData={otherData}
							record={phaseTaskRecord}
							allCampaignTasks={allCampaignTasks}
							phaseID={phaseTaskRecord?._id}
							taskRecord={phaseTaskRecord?.task}
							activeRowKey={phaseTaskRecord?.task?._id}
							extraData={allData}
							user={props.params?.user}
							userRole={props.params?.userRole}
						/>
					)}
					<DeliverableConfigurationModal
						open={deliverableModalOpen}
						setOpen={setDeliverableModalOpen}
						handleFormUpdate={handleDeliverableFormUpdate}
						handleModalClose={handleDeliverableModalClose}
						taskID={phaseTaskRecord?.task?._id}
						deliverableRecord={deliverableRecord}
						activeRowKey={deliverableRecord?._id}
						extraData={deliverableExtraData}
						currentProject={projectInfo}
						user={props.params?.user}
						userRole={props.params?.userRole}
						taskRecord={phaseTaskRecord?.task}
					/>
					<TemplateDisabledModal
						open={templateModalOpen}
						setOpen={setTemplateModalOpen}
						handleTemplateModalSave={handleTemplateModalSave}
						title={templateObject?.title}
						message={templateObject?.message}
						id={templateObject?.id}
						level={templateObject?.level}
					/>
				</div>
			)}
			{!projectInfo && !isLoading && (
				<NoAccess text={"This project does not exist."} />
			)}
			{!wellInfo && projectInfo && !isLoading && (
				<NoAccess text={"This well does not exist."} />
			)}
		</>
	);
}

OperationSettings.propTypes = {
	params: PropTypes.any,
};

export default OperationSettings;
